/**
 * Invite Landing — handles the magic-link sign-in flow before the rest of
 * the app boots. Rendered by main.tsx when the URL looks like a Firebase
 * email-action-link (mode=signIn + oobCode).
 *
 * Flow:
 *   1. signInWithEmailLink(auth, email, url) — completes the auth handshake.
 *   2. Read `token` from the URL query and call callFunction('acceptInvite').
 *      The Cloud Function consumes the token, sets custom claims, creates the
 *      profile + company_members rows, and writes an audit row.
 *   3. Set sessionStorage flag → AuthGate renders WelcomeFirstLogin next.
 *   4. Clean magic-link params from the URL so a refresh doesn't retry.
 *   5. Render children (the normal AuthGate-wrapped app).
 *
 * If the email isn't in localStorage (user opened the link on a different
 * device), prompt them to retype the address. If sign-in fails (expired,
 * revoked, malformed), show a friendly error with a "Request new invite" CTA.
 */
import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { Loader2, AlertTriangle, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { BrandLogo } from './auth/BrandLogo';

const EMAIL_FOR_SIGNIN_KEY = 'deepatomic_email_for_signin';
const PENDING_WELCOME_KEY = 'deepatomic_pending_welcome';

type Phase = 'starting' | 'need-email' | 'signing-in' | 'accepting' | 'done' | 'error';

interface Props {
  /** Rendered after the magic-link flow completes (success or graceful skip). */
  children: React.ReactNode;
}

/**
 * Synchronous URL probe. Returns true if the current URL looks like a Firebase
 * email-action-link callback. Used by main.tsx to decide whether to render
 * this component vs going straight to AuthGate.
 */
export function urlLooksLikeMagicLink(href: string = window.location.href): boolean {
  try {
    const url = new URL(href);
    return url.searchParams.get('mode') === 'signIn' && !!url.searchParams.get('oobCode');
  } catch { return false; }
}

/**
 * Extract the invite token from the landed URL — top-level `token` first
 * (app-origin rewritten links lift it there), else from INSIDE the nested
 * `continueUrl` param. The Firebase-HOSTED action handler forwards to the
 * app with mode/oobCode/apiKey appended but keeps the original continueUrl
 * (and the token inside it) as a still-encoded nested param — reading only
 * the top level silently skipped acceptInvite, so the signed-in user had no
 * claims and hit the private-beta Access Denied screen (wgjtheron invite,
 * 2026-06-10).
 */
export function inviteTokenFromUrl(href: string): string | null {
  try {
    const url = new URL(href);
    const top = url.searchParams.get('token');
    if (top) return top;
    const cont = url.searchParams.get('continueUrl');
    if (cont) {
      try { return new URL(cont).searchParams.get('token'); } catch { return null; }
    }
    return null;
  } catch {
    return null;
  }
}

/** Strip the Firebase magic-link query params after a successful sign-in. */
function cleanMagicLinkParams(): void {
  try {
    const url = new URL(window.location.href);
    for (const k of ['mode', 'oobCode', 'apiKey', 'continueUrl', 'lang', 'token']) {
      url.searchParams.delete(k);
    }
    const next = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
    window.history.replaceState({}, '', next);
  } catch { /* ignore */ }
}

/**
 * Map a Firebase sign-in error to a clear, actionable user-facing message.
 *
 * Bug #80 root cause is config-side: on a project upgraded to Google Cloud
 * Identity Platform (GCIP), the Firebase-hosted auth-handler page
 * (`https://<authDomain>/__/auth/action`) calls `GetProjectConfig` on
 * identitytoolkit.googleapis.com using only the public browser API key. On a
 * GCIP project that endpoint is OAuth-gated and returns 401 to an anonymous
 * browser, so the handler page errors out *before* it forwards the user to the
 * app's `continueUrl` — the user never reaches this component and sees a raw
 * 401 on the firebaseapp.com page.
 *
 * The durable fix is config-side (see docs/qa/invite-link-401.md). This mapping
 * is the in-app safety net: if any GCIP/Identity-Platform misconfiguration
 * surfaces inside the client (e.g. the link is rewritten to the app origin but
 * the email-link provider is still disabled, or a GetProjectConfig 401 ever
 * reaches here), show a specific, actionable message rather than a raw status
 * string.
 *
 * @param e The thrown error from `signInWithEmailLink` (or related calls).
 * @returns A human-readable, actionable message for the error UI.
 */
export function mapSignInError(e: unknown): string {
  const code = (e as { code?: string })?.code || '';
  const rawMessage = e instanceof Error ? e.message : '';
  const haystack = `${code} ${rawMessage}`.toLowerCase();

  // --- Expired / already-used / disabled (ordinary, expected cases) ---
  if (code.includes('expired-action-code')) {
    return 'This invite link has expired. Ask your admin to send a new one.';
  }
  if (code.includes('invalid-action-code')) {
    return 'This invite link is invalid or has already been used. Ask your admin to send a new one.';
  }
  if (code.includes('user-disabled')) {
    return 'Your account has been disabled. Contact your admin.';
  }

  // --- GCIP / Identity-Platform misconfiguration (bug #80) ---
  // GetProjectConfig 401 from identitytoolkit, or a missing/misconfigured
  // Identity-Platform project config. These are operator-fixable, not
  // user-fixable, so the message points the user at the admin explicitly.
  if (
    haystack.includes('getprojectconfig') ||
    haystack.includes('identitytoolkit') ||
    haystack.includes('project_not_found') ||
    haystack.includes('configuration_not_found') ||
    haystack.includes('project-not-found') ||
    haystack.includes('configuration-not-found')
  ) {
    return (
      'The invite sign-in service is misconfigured (Identity Platform). ' +
      'This is a server-side setting, not something you can fix — please ' +
      'contact your admin and reference bug #80 (invite-link 401).'
    );
  }

  // Email-link sign-in not enabled in the Identity-Platform provider config.
  // On a GCIP project, Email/Password + "Email link (passwordless sign-in)"
  // must be explicitly turned ON before signInWithEmailLink is permitted.
  if (code.includes('operation-not-allowed')) {
    return (
      'Passwordless email-link sign-in is not enabled for this project. ' +
      'Ask your admin to enable the Email-link provider in Identity Platform ' +
      '(bug #80).'
    );
  }

  // Malformed link reaching the client (e.g. the action link was not rewritten
  // to the app origin, so the params the SDK needs are missing/garbled).
  if (code.includes('argument-error')) {
    return (
      'This invite link is malformed or incomplete. Ask your admin to send a ' +
      'fresh invite, or copy the full link from the email into your browser.'
    );
  }

  // Network / 401 fall-throughs — surface something actionable, not a bare code.
  if (haystack.includes('401') || code.includes('network-request-failed')) {
    return (
      'The invite sign-in request was rejected (authorization error). This is ' +
      'usually a server-side configuration issue — please contact your admin ' +
      'and reference bug #80 (invite-link 401).'
    );
  }

  // Last resort: prefer the SDK message if present, else a generic line.
  return rawMessage || 'Sign-in failed. Please contact your admin for a new invite.';
}

/** Inline copy of AdminPanel's callFunction — proxied through the same /api endpoint. */
async function callFunction<T = unknown>(name: string, data: unknown = {}): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`/api/functions/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    // The dispatcher destructures `req.body.data` — the payload MUST be
    // wrapped exactly like AdminPanel's callFunction does. This inline copy
    // had diverged (sent the payload bare), so acceptInvite received
    // data=undefined → 400 'token required' → the signed-in invitee kept
    // no claims and hit the private-beta Access Denied screen.
    body: JSON.stringify({ data: data ?? {} }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error((json && (json.error || json.message)) || `Function ${name} failed: ${res.status}`);
  return json as T;
}

export function InviteLanding({ children }: Props) {
  const [phase, setPhase] = useState<Phase>('starting');
  const [error, setError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    const stored = (() => { try { return localStorage.getItem(EMAIL_FOR_SIGNIN_KEY) || ''; } catch { return ''; } })();
    if (stored) {
      void completeSignIn(stored);
    } else {
      setPhase('need-email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function completeSignIn(email: string) {
    setPhase('signing-in');
    setError(null);
    try {
      const { signInWithEmailLink, isSignInWithEmailLink } = await import('firebase/auth');
      const url = window.location.href;
      if (!isSignInWithEmailLink(auth, url)) {
        // Defensive — main.tsx already pre-checked. Pass through.
        setPhase('done');
        return;
      }
      await signInWithEmailLink(auth, email.trim(), url);
      try { localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY); } catch { /* ignore */ }

      // Consume the invite token (best-effort — sign-in already succeeded).
      const token = inviteTokenFromUrl(url);
      if (token) {
        setPhase('accepting');
        try {
          await callFunction('acceptInvite', { token });
        } catch (e) {
          // Token may be already-accepted (idempotent re-click), expired, or
          // revoked. Sign-in succeeded, so let the user through; the welcome
          // screen will guide them.
          console.warn('acceptInvite failed (proceeding anyway):', e);
        }
      }

      try { sessionStorage.setItem(PENDING_WELCOME_KEY, '1'); } catch { /* ignore */ }
      cleanMagicLinkParams();
      setPhase('done');
    } catch (e) {
      setError(mapSignInError(e));
      setPhase('error');
    }
  }

  if (phase === 'done') return <>{children}</>;

  return (
    <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
      <div className="max-w-md w-full mx-4 space-y-6">
        <div className="text-center space-y-3">
          {/* Theme-adaptive DA wordmark (currentColor) — the old hardcoded
              Atom glyph + text-white washed out on the light theme. */}
          <div className="flex justify-center text-text-primary">
            <BrandLogo height={36} />
          </div>
          <div>
            <h1 className="sr-only">Datacrate</h1>
            <p className="text-[9px] text-text-muted uppercase tracking-widest mt-1">Completing your invite</p>
          </div>
        </div>

        <div className="border-2 border-border bg-background p-6 space-y-4">
          {(phase === 'starting' || phase === 'signing-in' || phase === 'accepting') && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="w-6 h-6 text-text-primary animate-spin" />
              <div className="text-[10px] text-text-secondary uppercase tracking-widest">
                {phase === 'starting' && 'Loading…'}
                {phase === 'signing-in' && 'Signing you in…'}
                {phase === 'accepting' && 'Activating your account…'}
              </div>
            </div>
          )}

          {phase === 'need-email' && (
            <div className="space-y-3">
              <div className="text-[10px] text-text-secondary uppercase tracking-widest">
                Confirm the email this invite was sent to:
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                  placeholder="you@company.com" autoComplete="email" autoFocus
                  className="w-full h-10 pl-10 pr-3 text-sm border-2 border-border bg-background text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary rounded-md"
                />
              </div>
              <Button
                className="w-full h-10 rounded-md uppercase tracking-widest text-[10px] font-bold"
                onClick={() => emailInput.trim() && void completeSignIn(emailInput)}
                disabled={!emailInput.trim()}
              >
                Continue
              </Button>
            </div>
          )}

          {phase === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-warning">Could not complete invite</div>
                  <div className="text-[10px] text-text-secondary leading-relaxed">{error}</div>
                </div>
              </div>
              <div className="text-[9px] text-text-muted leading-relaxed">
                Ask the admin who invited you to send a new link, or contact{' '}
                <span className="text-text-secondary">admin@datacrate.io</span>.
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  className="flex-1 h-9 rounded-md uppercase tracking-widest text-[9px]"
                  onClick={() => { cleanMagicLinkParams(); window.location.href = '/'; }}
                >
                  Back to sign-in
                </Button>
                <Button
                  size="sm"
                  className="flex-1 h-9 rounded-md uppercase tracking-widest text-[9px]"
                  onClick={() => { window.location.href = 'mailto:admin@datacrate.io?subject=Request%20new%20invite'; }}
                >
                  Request new invite
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
